import {useEffect, useMemo} from 'react';
import {useQuery, useQueries, useInfiniteQuery, useMutation} from '@tanstack/react-query';
import type {UseQueryResult} from '@tanstack/react-query';
import {
    fetchProjectInfo,
    fetchEpisodeList,
    fetchAssetInfo,
    fetchAssetExtractStatus,
    fetchMemberRoleList,
    fetchDeletedEpisodeList,
    renameEpisode,
    deleteEpisodes,
    restoreEpisodes,
    purgeEpisodes,
} from '@/services/projects';
import {fetchScriptDetail} from '@/services/script';
import {PARSE_STATUS, ScriptStatus} from '@/services/script/interface';
import {useProjectStore} from '@/store/projectStore';
import {usePermission} from '@/hooks/usePermission';
import {PERMISSIONS} from '@/types/permission';
import {queryClient} from '@/utils/queryClient';
import type {
    ProjectOverviewData,
    EpisodeItem,
    DeletedEpisodeItem,
    ProjectInfoData,
    AssetInfoData,
    PointInfo,
} from '@/types/project';
import {AssetStatus, AssetExtractStatus} from '@/types/project';
import type {ProjectId} from '@/types';
import {RequestError} from '@/services/request/errors';
import {STATUS_CODE} from '@/constants';

/** 回收箱列表每页条数，服务端 pageSize 上限为 100；抽屉内一行 4 张卡，20 条正好 5 行 */
const RECYCLE_PAGE_SIZE = 20;

interface UseProjectOverviewReturn {
    data: ProjectOverviewData;
    isLoading: boolean;
    isError: boolean;
    /** project/info 单独的错误状态 */
    infoError: boolean;
    /** 项目已被删除 */
    isProjectDeleted: boolean;
    /** asset/info 单独的错误状态 */
    assetsError: boolean;
    refetch: () => void;
}

const PAGE_SIZE = 10;

const isProjectDeletedError = (error: unknown): error is RequestError =>
    error instanceof RequestError && error.status === STATUS_CODE.PROJECT_NOT_FOUND;

export const projectKeys = {
    all: ['projectHome'] as const,
    info: (id: ProjectId) => [...projectKeys.all, 'info', id] as const,
    script: (id: ProjectId) => [...projectKeys.all, 'script', id] as const,
    // keyword 进 key：换关键词等于换一个查询，分页天然从第 1 页重新开始
    episodes: (id: ProjectId, keyword = '') =>
        [...projectKeys.all, 'episodes', id, keyword] as const,
    deletedEpisodes: (id: ProjectId, keyword = '') =>
        [...projectKeys.all, 'deletedEpisodes', id, keyword] as const,
    assets: (id: ProjectId) => [...projectKeys.all, 'assets', id] as const,
    roles: ['memberRoles'] as const,
};

interface UseEpisodeListReturn {
    episodes: EpisodeItem[];
    /** keyword 为空时是剧集实际条数，非空时是命中数 */
    total: number;
    agentId?: string;
    /** 回收箱保留天数，后端未下发时由调用方兜底 */
    recycleRetentionDays?: number;
    isLoading: boolean;
    isError: boolean;
    refetch: () => void;
    isNextPageError: boolean;
    hasMore: boolean;
    loadMore: () => void;
}

/**
 * 获取剧集列表，支持滚动加载更多
 */
export const useEpisodeList = (projectId: ProjectId, keyword = ''): UseEpisodeListReturn => {
    const {data, isLoading, isError, refetch, fetchNextPage, hasNextPage, isFetchNextPageError} =
        useInfiniteQuery({
            queryKey: projectKeys.episodes(projectId, keyword),
            queryFn: ({pageParam}) => fetchEpisodeList(projectId, pageParam, PAGE_SIZE, keyword),
            initialPageParam: 1,
            getNextPageParam: (lastPage, allPages) => {
                const loaded = allPages.reduce((sum, p) => sum + (p.episodesList?.length ?? 0), 0);
                return loaded < lastPage.total ? allPages.length + 1 : undefined;
            },
            enabled: !!projectId,
            refetchOnMount: 'always',
        });

    const episodes = useMemo(() => data?.pages.flatMap((p) => p.episodesList ?? []) ?? [], [data]);
    const agentId = data?.pages[0]?.agentId;

    return {
        episodes,
        total: data?.pages[0]?.total ?? 0,
        agentId,
        recycleRetentionDays: data?.pages[0]?.recycleRetentionDays,
        isLoading,
        isError: isError && !data,
        refetch: () => {
            void refetch();
        },
        isNextPageError: isFetchNextPageError,
        hasMore: hasNextPage,
        loadMore: () => {
            void fetchNextPage();
        },
    };
};

/**
 * 并行调用 2 个接口（project/info、asset/info），聚合为 ProjectOverviewData
 */
export const useProjectOverview = (projectId: ProjectId): UseProjectOverviewReturn => {
    const setProjectInfo = useProjectStore((state) => state.setProjectInfo);
    const setRoleList = useProjectStore((state) => state.setRoleList);
    const results = useQueries({
        queries: [
            {
                queryKey: projectKeys.info(projectId),
                queryFn: () => fetchProjectInfo(projectId),
                enabled: !!projectId,
                refetchOnMount: 'always',
                refetchOnWindowFocus: true,
            },
            {
                queryKey: projectKeys.assets(projectId),
                queryFn: () => fetchAssetInfo(projectId),
                enabled: !!projectId,
                refetchOnMount: 'always',
            },
        ],
    });

    // 角色列表独立请求，不参与 isLoading 判断，不阻塞页面渲染
    const rolesQuery = useQuery({
        queryKey: projectKeys.roles,
        queryFn: fetchMemberRoleList,
    });

    const [infoQuery, assetsQuery] = results as [
        UseQueryResult<ProjectInfoData>,
        UseQueryResult<AssetInfoData>,
    ];

    const isLoading = results.some((q) => q.isLoading);
    const isError = results.some((q) => q.isError);

    const infoData = infoQuery.data;
    const assetsData = assetsQuery.data;
    const rolesData = rolesQuery.data;

    // 更新projectInfo
    useEffect(() => {
        if (infoData?.projectInfo) {
            setProjectInfo(infoData.projectInfo);
        }
    }, [infoData?.projectInfo, setProjectInfo]);

    // 更新角色列表
    useEffect(() => {
        if (rolesData) {
            setRoleList(rolesData);
        }
    }, [rolesData, setRoleList]);

    // 资产提取中时轮询状态，提取成功后重新获取资产信息
    const canExtract = usePermission(PERMISSIONS.ASSET.EXTRACT);
    const isExtracting = assetsData?.assetStatus === AssetStatus.Parsing;
    const extractTaskId = assetsData?.taskId ?? '';
    const pollingQuery = useQuery({
        queryKey: [...projectKeys.assets(projectId), 'polling', extractTaskId],
        queryFn: () => fetchAssetExtractStatus(projectId, extractTaskId),
        enabled: !!projectId && isExtracting && !!extractTaskId && canExtract,
        refetchInterval: isExtracting && canExtract ? 3000 : false,
    });

    useEffect(() => {
        if (pollingQuery.data?.status === AssetExtractStatus.Success) {
            void queryClient.invalidateQueries({queryKey: projectKeys.assets(projectId)});
        }
    }, [pollingQuery.data?.status, projectId]);

    // 剧本解析中时轮询 script/detail，解析成功后重新获取资产信息
    const isParsing = assetsData?.scriptInfo?.status === ScriptStatus.DEFAULT;
    const scriptPollingQuery = useQuery({
        queryKey: [...projectKeys.assets(projectId), 'scriptParsing'],
        queryFn: () => fetchScriptDetail({projectId, source: 'workbench'}),
        enabled: !!projectId && isParsing,
        refetchInterval: isParsing ? 3000 : false,
    });

    useEffect(() => {
        if (scriptPollingQuery.data?.parseInfo?.status === PARSE_STATUS.SUCCESS) {
            void queryClient.invalidateQueries({queryKey: projectKeys.assets(projectId)});
        }
    }, [scriptPollingQuery.data?.parseInfo?.status, projectId]);

    const data: ProjectOverviewData = {
        projectInfo: infoData?.projectInfo,
        progressInfo: infoData?.progressInfo,
        pointInfo: infoData?.pointInfo,
        members: infoData?.members,
        script: assetsData?.scriptInfo,
        assets: assetsData,
    };

    return {
        data,
        isLoading,
        isError,
        infoError: infoQuery.isError,
        isProjectDeleted: isProjectDeletedError(infoQuery.error),
        assetsError: assetsQuery.isError,
        refetch: () => {
            // eslint-disable-next-line array-callback-return
            results.forEach((q) => q.refetch());
        },
    };
};

export const invalidateProjectInfo = (projectId: ProjectId) =>
    queryClient.invalidateQueries({queryKey: projectKeys.info(projectId)});

/**
 * 项目维度积分 Hook（从 project/info 派生 pointInfo）
 */
export const useProjectPoints = (projectId: ProjectId) => {
    return useQuery<ProjectInfoData, Error, PointInfo | undefined>({
        queryKey: projectKeys.info(projectId),
        queryFn: () => fetchProjectInfo(projectId),
        enabled: !!projectId,
        select: (data) => data.pointInfo,
    });
};

interface UseDeletedEpisodeListReturn {
    list: DeletedEpisodeItem[];
    total: number;
    /** 回收箱保留天数，后端未下发时由调用方兜底 */
    recycleRetentionDays?: number;
    isLoading: boolean;
    isError: boolean;
    isNextPageError: boolean;
    hasMore: boolean;
    loadMore: () => void;
    refetch: () => void;
}

/**
 * 回收站列表，keyword 进 queryKey，enabled 由回收站 Tab 是否激活驱动
 */
export const useDeletedEpisodeList = (
    projectId: ProjectId,
    keyword: string,
    enabled: boolean,
): UseDeletedEpisodeListReturn => {
    const {data, isLoading, isError, refetch, fetchNextPage, hasNextPage, isFetchNextPageError} =
        useInfiniteQuery({
            queryKey: projectKeys.deletedEpisodes(projectId, keyword),
            queryFn: ({pageParam}) =>
                fetchDeletedEpisodeList(projectId, pageParam, RECYCLE_PAGE_SIZE, keyword),
            initialPageParam: 1,
            getNextPageParam: (lastPage, allPages) => {
                const loaded = allPages.reduce((sum, p) => sum + p.list.length, 0);
                return loaded < lastPage.total ? allPages.length + 1 : undefined;
            },
            enabled: enabled && !!projectId,
            // 回收站是多人共享数据，缓存旧了会对已失效的 id 发起操作
            staleTime: 0,
        });

    const list = useMemo(() => data?.pages.flatMap((p) => p.list) ?? [], [data]);

    return {
        list,
        total: data?.pages[0]?.total ?? 0,
        recycleRetentionDays: data?.pages[0]?.recycleRetentionDays,
        isLoading,
        // 已有数据时的失败交给分页重试，不整屏切失败态
        isError: isError && !data,
        isNextPageError: isFetchNextPageError,
        hasMore: hasNextPage,
        loadMore: () => {
            void fetchNextPage();
        },
        refetch: () => {
            void refetch();
        },
    };
};

/**
 * 剧集写操作后统一失效：集数列表、回收站列表、项目信息
 */
const invalidateEpisodeQueries = (projectId: ProjectId) => {
    // 不带 keyword，按前缀失效所有关键词下的查询，否则清空搜索框后看到的是操作前的旧缓存
    void queryClient.invalidateQueries({queryKey: [...projectKeys.all, 'episodes', projectId]});
    void queryClient.invalidateQueries({
        queryKey: [...projectKeys.all, 'deletedEpisodes', projectId],
    });
    void queryClient.invalidateQueries({queryKey: projectKeys.info(projectId)});
};

/**
 * 剧集重命名
 */
export const useRenameEpisode = (projectId: ProjectId) =>
    useMutation({
        mutationFn: (params: {episodeId: number; title: string}) =>
            renameEpisode({projectId, ...params}),
        onSuccess: () => invalidateEpisodeQueries(projectId),
    });

/**
 * 批量移入回收箱，单集删除传长度为 1 的数组
 */
export const useDeleteEpisodes = (projectId: ProjectId) =>
    useMutation({
        mutationFn: (params: {episodeIds: number[]}) => deleteEpisodes({projectId, ...params}),
        onSuccess: () => invalidateEpisodeQueries(projectId),
    });

/**
 * 批量恢复
 */
export const useRestoreEpisodes = (projectId: ProjectId) =>
    useMutation({
        mutationFn: (params: {episodeIds: number[]}) => restoreEpisodes({projectId, ...params}),
        onSuccess: () => invalidateEpisodeQueries(projectId),
    });

/**
 * 永久删除 / 清空回收箱
 */
export const usePurgeEpisodes = (projectId: ProjectId) =>
    useMutation({
        mutationFn: (params: {episodeIds?: number[]; all?: boolean}) =>
            purgeEpisodes({projectId, ...params}),
        onSuccess: () => invalidateEpisodeQueries(projectId),
    });
